import _ from 'lodash'
import { createContext, useCallback, useMemo, useReducer } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Container, Stack, Step, StepLabel, Stepper } from '@mui/material'

import { useIntro } from '@/hooks/useIntro'

import { CreateConsultationIntro } from '@/utils/onboarding/intro'

import ConfirmationStep from './containers/ConfirmationStep'
import SelectPatientStep from './containers/SelectPatientStep'
import SelectSpecialityStep from './containers/SelectSpecialityStep'
import SelectAppointmentStep from './containers/SelectAppointmentStep'
import { previousStep } from 'intro.js/src/core/steps'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

export enum CreateConsultationStepEnum {
  SELECT_PATIENT = 'SELECT_PATIENT',
  SELECT_SPECIALITY = 'SELECT_SPECIALITY',
  SELECT_APPOINTMENT = 'SELECT_APPOINTMENT',
  CONFIRMATION = 'CONFIRMATION',
}

type StepOrdinal = 0 | 1 | 2

type CreateConsultationStepOrdinal = {
  [CreateConsultationStepEnum.SELECT_PATIENT]: StepOrdinal
  [CreateConsultationStepEnum.SELECT_SPECIALITY]: StepOrdinal
  [CreateConsultationStepEnum.SELECT_APPOINTMENT]: StepOrdinal
}

type CreateConsultationStepperConfig = {
  excludedSteps?: CreateConsultationStepEnum[]
  showOnlyAvailability?: boolean
}

export type CreateConsultationStepperValues = {
  medecin?: string
  medecinScheduleConsultationReason?: string
  meetingOnly?: boolean
  patient?: string
  patients?: string[]
  isGroupConsultation?: boolean
  pds?: string
  reason?: string
  speciality?: string
  startDate?: string
  expectedDuration?: number
  eventType?: string
  waitingRoom?: string
  network?: string
}

type CreateConsultationStepperContextType = {
  activeStep: number
  config?: CreateConsultationStepperConfig
  previousStep: number
  stepCount: number
  initialValues: CreateConsultationStepperValues
  values: CreateConsultationStepperValues
  setValue: (field: string, value?: string | number | boolean) => void
  goNextStep: () => void
  goPreviousStep: () => void
}

type Action = { type: 'next' } | { type: 'back' } | { type: 'setValue'; payload: Partial<CreateConsultationStepperValues> }

type State = {
  activeStep: number
  completedSteps: number[]
  previousStep: number
  initialValues: CreateConsultationStepperValues
  values: CreateConsultationStepperValues
}

function reducer(state: State, action: Action) {
  const previousStep = state.activeStep
  switch (action.type) {
    case 'next': {
      const activeStep = state.activeStep + 1
      return { ...state, activeStep, completedSteps: state.completedSteps.concat(activeStep - 1), previousStep }
    }
    case 'back': {
      const activeStep = state.activeStep - 1
      return {
        ...state,
        activeStep,
        completedSteps: state.completedSteps.filter((step) => step !== activeStep + 1),
        previousStep: state.activeStep,
      }
    }
    case 'setValue':
      return { ...state, values: { ...state.values, ...action.payload } }
    default:
      return state
  }
}

export const CreateConsultationStepperContext = createContext<CreateConsultationStepperContextType>({
  config: {},
  activeStep: 0,
  previousStep: 0,
  stepCount: 0,
  setValue() {},
  initialValues: {},
  values: {},
  goNextStep() {},
  goPreviousStep() {},
})

type CreateConsultationStepperProps = {
  config?: CreateConsultationStepperConfig
  initialValues?: CreateConsultationStepperValues
  stepOrdinal?: CreateConsultationStepOrdinal
  onComplete?: () => void
}

export function CreateConsultationStepper(props: CreateConsultationStepperProps) {
  const { config, initialValues: initialValuesProp, onComplete, stepOrdinal } = props
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const isMobile = useIsMobile()

  const excludedSteps = config?.excludedSteps
  const steps = [
    {
      id: CreateConsultationStepEnum.SELECT_PATIENT,
      label: t('page.createConsultation.step.patientSelection.label'),
      component: SelectPatientStep,
      weight: stepOrdinal?.[CreateConsultationStepEnum.SELECT_PATIENT] ?? 0,
    },
    {
      id: CreateConsultationStepEnum.SELECT_SPECIALITY,
      label: t('page.createConsultation.step.specialitySelection.label'),
      component: SelectSpecialityStep,
      weight: stepOrdinal?.[CreateConsultationStepEnum.SELECT_SPECIALITY] ?? 1,
    },
    {
      id: CreateConsultationStepEnum.SELECT_APPOINTMENT,
      label: t('page.createConsultation.step.appointmentSelection.label'),
      component: SelectAppointmentStep,
      weight: stepOrdinal?.[CreateConsultationStepEnum.SELECT_APPOINTMENT] ?? 2,
    },
    {
      id: CreateConsultationStepEnum.CONFIRMATION,
      label: t('page.createConsultation.step.confirmation.label'),
      component: ConfirmationStep,
      componentProps: { onComplete },
      weight: stepOrdinal ? Math.max(..._.values(stepOrdinal)) + 1 : 3,
    },
  ].filter((step) => !excludedSteps?.includes(step.id))

  steps.sort((stepA, stepB) => stepA.weight - stepB.weight)

  const stepCount = steps.length

  const initialCompletedSteps: number[] = []
  if (initialValuesProp?.patient) initialCompletedSteps.push(steps.findIndex((step) => step.id === CreateConsultationStepEnum.SELECT_PATIENT))
  if (initialValuesProp?.speciality) initialCompletedSteps.push(steps.findIndex((step) => step.id === CreateConsultationStepEnum.SELECT_SPECIALITY))
  initialCompletedSteps.sort((a, b) => a - b)

  const [{ activeStep, completedSteps, previousStep, initialValues, values }, stepperDispatch] = useReducer(reducer, {
    activeStep: _.first(_.difference(_.range(0, stepCount), initialCompletedSteps)) ?? 0,
    completedSteps: initialCompletedSteps,
    previousStep: 0,
    initialValues: _.merge({}, { ...initialValuesProp, startDate: initialValuesProp?.startDate ?? searchParams.get('startDate') ?? undefined }),
    values: _.merge({}, { ...initialValuesProp, startDate: initialValuesProp?.startDate ?? searchParams.get('startDate') ?? undefined }),
  })

  useIntro(CreateConsultationIntro[activeStep])

  const setValue = useCallback((field: string, value?: unknown) => {
    stepperDispatch({ type: 'setValue', payload: { [field]: value } })
  }, [])

  const goPreviousStep = useCallback(() => {
    stepperDispatch({ type: 'back' })
  }, [])

  const goNextStep = useCallback(() => {
    stepperDispatch({ type: 'next' })
  }, [])

  const providerValue: CreateConsultationStepperContextType = useMemo(
    () => ({
      activeStep,
      config,
      previousStep,
      stepCount,
      setValue,
      initialValues,
      values,
      goNextStep,
      goPreviousStep,
    }),
    [activeStep, config, previousStep, stepCount, setValue, initialValues, values, goNextStep, goPreviousStep]
  )

  return (
    <CreateConsultationStepperContext.Provider value={providerValue}>
      <Container sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!isMobile && (
          <Stack sx={{ p: '32px 16px' }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ flex: 1 }}>
              {steps.map((step, stepIdx) => (
                <Step key={step.label} completed={completedSteps.includes(stepIdx)}>
                  <StepLabel>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Stack>
        )}
        {steps.map((step) => {
          const StepComponent = step.component
          return activeStep === step.weight ? <StepComponent key={StepComponent.name} {...step.componentProps} /> : null
        })}
      </Container>
    </CreateConsultationStepperContext.Provider>
  )
}
